import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { formatCurrency } from '../../utils/formatters';

// ═══════════════════════════════════════════════════════════
// Constants & Helpers
// ═══════════════════════════════════════════════════════════
const LOAN_STATUS_COLORS = {
  active: '#059669', overdue: '#dc2626', defaulted: '#991b1b',
  closed: '#64748b', 'written-off': '#64748b', default: '#3b82f6'
};
const SCHEDULE_STATUS_COLORS = {
  paid: '#10b981', overdue: '#ef4444', partial: '#f59e0b', default: '#94a3b8'
};
const getLoanStatusColor = (s) => LOAN_STATUS_COLORS[s] || LOAN_STATUS_COLORS.default;
const getScheduleStatusColor = (s) => SCHEDULE_STATUS_COLORS[s] || SCHEDULE_STATUS_COLORS.default;

const INTEREST_RATE_ROWS = [
  { days: 0, rate: 0, note: 'Disbursement (no interest)' },
  { days: 1, rate: 14, note: 'First interest day' },
  { days: 10, rate: 14, note: 'Minimum rate' },
  { days: 14, rate: 14, note: 'Last day of base rate' },
  { days: 15, rate: 15, note: 'Tiered starts' },
  { days: 16, rate: 16, note: '+1%/day' },
  { days: 20, rate: 20, note: '' },
  { days: 22, rate: 22, note: '' },
  { days: 30, rate: 30, note: 'Maximum cap' },
  { days: 31, rate: 30, note: 'Default phase' },
  { days: 35, rate: 30, note: 'Capped at 30%' },
];

const PENALTY_TIERS = [
  { tier: 0, label: 'None', percent: 0, days: '—', color: '#94a3b8' },
  { tier: 1, label: 'Tier 1', percent: 5, days: 'Day 31–44', color: '#f59e0b' },
  { tier: 2, label: 'Tier 2', percent: 10, days: 'Day 45–59', color: '#ea580c' },
  { tier: 3, label: 'Tier 3', percent: 15, days: 'Day 60+', color: '#dc2626' },
];

const TABS = [
  { key: 'info', label: '📄 Loan Info' },
  { key: 'schedule', label: '📅 Repayment Schedule' },
  { key: 'interest', label: '📊 Interest History' },
  { key: 'payments', label: '💰 Payment History' },
];

const CLOSED_LOAN_TABS = [
  { key: 'info', label: '📄 Loan Info' },
  { key: 'schedule', label: '📅 Schedule' },
  { key: 'interest', label: '📊 Interest History' },
  { key: 'payments', label: '💰 Payments' },
  { key: 'closure', label: '🔒 Closure' },
];

const ITEMS_PER_PAGE = 25;

// ═══════════════════════════════════════════════════════════
// Pre-computed Styles
// ═══════════════════════════════════════════════════════════
const S = {
  searchWrapper: { position: 'relative' },
  searchIcon: { position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '14px' },
  searchInput: { paddingLeft: '32px', height: '36px', width: '300px', fontSize: '13px', border: '1px solid #e2e8f0', borderRadius: '6px', outline: 'none', background: '#fff' },
  infoBanner: { background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#1e40af', lineHeight: 1.6 },
  closedBanner: { background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', border: '1px solid #86efac', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#166534', lineHeight: 1.6 },
  loanNumber: { fontFamily: 'monospace', fontSize: '15px', color: 'var(--primary)' },
  tabBorder: { marginBottom: '20px', borderColor: '#e2e8f0' },
  tc: { padding: '10px 12px' },
  tcr: { padding: '10px 12px', textAlign: 'right' },
  tcs: { padding: '8px 12px' },
  tcsr: { padding: '8px 12px', textAlign: 'right' },
  tcc: { padding: '8px 12px', textAlign: 'center' },
  stickyHeader: { position: 'sticky', top: 0, background: '#f8fafc', zIndex: 10 },
  scrollContainer: { maxHeight: '500px', overflowY: 'auto' },
  penaltyBox: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 },
  earlyRepayBox: { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8 },
  closedBox: { background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8 },
  frozenBox: { background: '#f0f9ff', border: '2px solid #7dd3fc', borderRadius: 8 },
  rateTableCard: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 },
  pageBtn: { padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13, minWidth: 36, textAlign: 'center' },
  pageBtnActive: { padding: '6px 12px', border: '1px solid #3b82f6', borderRadius: 6, background: '#3b82f6', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, minWidth: 36, textAlign: 'center' },
  pageBtnDisabled: { padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#f1f5f9', color: '#94a3b8', cursor: 'not-allowed', fontSize: 13, minWidth: 36, textAlign: 'center' },
  closedLoanHeader: { padding: '10px 16px', background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderBottom: '1px solid #e5e7eb' },
  closedLoanBorder: { borderRadius: 10, overflow: 'hidden', border: '2px solid #10b981' },
  closedListHeader: { background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', borderBottom: '2px solid #e2e8f0' },
  statsCard: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px' },
  filterRow: { display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' },
  filterSelect: { padding: '6px 28px 6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, background: '#fff', cursor: 'pointer', appearance: 'auto' },
  filterLabel: { fontSize: 12, color: '#64748b', fontWeight: 500 },
  dateInput: { padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, background: '#fff' },
  exportBtn: { padding: '6px 14px', background: '#059669', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 500 },
  clearBtn: { padding: '5px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, background: '#fff', cursor: 'pointer', color: '#64748b' },
  activeListHeader: { background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', borderBottom: '2px solid #e2e8f0' },
  activeLoanHeader: { padding: '10px 16px', background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)', borderBottom: '1px solid #e5e7eb' },
  overdueLoanHeader: { padding: '10px 16px', background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)', borderBottom: '1px solid #e5e7eb' },
  summaryGrid: { padding: '12px 16px', fontSize: 13 },
};

const V = {
  cursorPointer: { cursor: 'pointer' },
  loanNumCell: { fontWeight: 700, fontFamily: 'monospace', color: 'var(--primary)', fontSize: 13 },
  clientIdMono: { fontFamily: 'monospace', fontSize: 11 },
  phoneCell: { fontSize: 12, color: '#475569', whiteSpace: 'nowrap' },
  rateAmber: { color: '#f59e0b', fontWeight: 600 },
  penaltyZero: { color: '#94a3b8', fontWeight: 400 },
  penaltyRed: { color: '#dc2626', fontWeight: 700 },
  penaltyOrange: { color: '#ea580c', fontWeight: 700 },
  activeBalanceBlue: { fontWeight: 'bold', color: '#2563eb' },
  remainingPurple: { fontWeight: 'bold', color: '#7c3aed' },
  owedRed: { fontWeight: 'bold', color: '#dc2626' },
  owedGreen: { fontWeight: 'bold', color: '#10b981' },
  daysBold: { fontWeight: 600 },
  overdueRed: { color: '#dc2626', fontWeight: 600 },
  tcNote: { ...S.tc, color: '#64748b', fontSize: 12 },
  tcNoteAmber: { ...S.tc, color: '#f59e0b', fontSize: 12 },
  tcrAmber: { ...S.tcr, color: '#f59e0b', fontWeight: 600 },
  tcrGreen: { ...S.tcr, color: '#059669' },
  tcrRed: { ...S.tcr, color: '#dc2626', fontWeight: 700 },
  tcrRedBig: { ...S.tcr, color: '#dc2626', fontWeight: 700, fontSize: '15px' },
  tcrPurpleBig: { ...S.tcr, fontWeight: 700, color: '#7c3aed', fontSize: '15px' },
  maturityRow: { background: '#eff6ff', fontWeight: 'bold', fontSize: '14px' },
  maturityNote: { ...S.tc, color: '#3b82f6', fontSize: 12 },
  accruedRow: { background: '#f0fdf4', fontWeight: 'bold' },
  accruedNote: { ...S.tc, color: '#059669', fontSize: 12 },
  remainingRow: { background: '#faf5ff', fontWeight: 'bold' },
  remainingLabel: { color: '#7c3aed' },
  remainingNote: { ...S.tc, color: '#7c3aed', fontSize: 12 },
  totalOwedRow: { background: '#fef2f2' },
  totalOwedLabel: { ...S.tc, fontWeight: 600 },
  paidRow: { background: '#ecfdf5' },
  paidLabel: { ...S.tc, fontWeight: 600 },
  paidValue: { ...S.tcr, color: '#059669' },
  progressBarBg: { marginTop: 4, height: 6, background: '#e9d5ff', borderRadius: 3, overflow: 'hidden', width: '100%', maxWidth: 200 },
  progressBarFill: { height: '100%', background: 'linear-gradient(90deg, #7c3aed, #a78bfa)', borderRadius: 3 },
  progressLabel: { color: '#7c3aed', marginTop: 2 },
  rateAmberLg: { color: '#f59e0b' },
  penaltyRedLg: { color: '#dc2626' },
  penaltyGrayLg: { color: '#94a3b8' },
  schedulePaid: { background: '#f0fdf4' },
  scheduleOverdue: { background: '#fef2f2' },
  scheduleTotalDue: { ...S.tcsr, fontWeight: 600 },
  scheduleStatusCenter: { ...S.tcs, textAlign: 'center' },
  fullyPaid: { color: '#059669', fontWeight: 700 },
  tableHeaderBg: { background: '#f8fafc' },
  rateTableHeaderBg: { background: '#e2e8f0' },
  rateCellCenter: { padding: '4px 10px', textAlign: 'center', color: '#f59e0b' },
  rateCellRight: { padding: '4px 10px', textAlign: 'right' },
  rateCellLeft: { padding: '4px 10px' },
  rateCellNote: { padding: '4px 10px', textAlign: 'center', color: '#64748b' },
  rateRowActive: { background: '#dbeafe', fontWeight: 700 },
  rateRowInactive: { background: 'transparent', fontWeight: 400 },
  rateRowZero: { background: '#f1f5f9', color: '#94a3b8' },
  frozenLabel: { color: '#0284c7', fontWeight: 700 },
  frozenValue: { color: '#0284c7', fontWeight: 600 },
  closedStatusBadge: { background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600 },
  writtenOffBadge: { background: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600 },
  statValueGreen: { color: '#059669', fontWeight: 700, fontSize: '18px' },
  statValueRed: { color: '#dc2626', fontWeight: 700, fontSize: '18px' },
  statValueBlue: { color: '#2563eb', fontWeight: 700, fontSize: '18px' },
  statValuePurple: { color: '#7c3aed', fontWeight: 700, fontSize: '18px' },
  statValueAmber: { color: '#d97706', fontWeight: 700, fontSize: '18px' },
  closureRow: { background: '#f0fdf4' },
  closureLabel: { ...S.tc, fontWeight: 600, color: '#065f46' },
  closureValue: { ...S.tcr, color: '#065f46' },
  tierBadgeNone: { background: '#f1f5f9', color: '#94a3b8', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, display: 'inline-block' },
  tierBadge1: { background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, display: 'inline-block' },
  tierBadge2: { background: '#ffedd5', color: '#9a3412', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, display: 'inline-block' },
  tierBadge3: { background: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, display: 'inline-block' },
  statusActive: { background: '#dcfce7', color: '#166534', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 },
  statusOverdue: { background: '#fee2e2', color: '#991b1b', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 },
  statusDefaulted: { background: '#fecaca', color: '#7f1d1d', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 },
};

// ═══════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════
const getPenaltyTierLabel = (tier) => {
  switch (tier) {
    case 1: return 'Tier 1 (5%)';
    case 2: return 'Tier 2 (10%)';
    case 3: return 'Tier 3 (15%)';
    default: return 'None';
  }
};

const getPenaltyTierBadgeStyle = (tier) => {
  switch (tier) {
    case 1: return V.tierBadge1;
    case 2: return V.tierBadge2;
    case 3: return V.tierBadge3;
    default: return V.tierBadgeNone;
  }
};

const getStatusBadgeStyle = (status) => {
  switch (status) {
    case 'active': return V.statusActive;
    case 'overdue': return V.statusOverdue;
    case 'defaulted': return V.statusDefaulted;
    default: return V.statusActive;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatPhone = (phone) => {
  if (!phone) return '—';
  const p = String(phone).replace(/\D/g, '');
  if (p.length === 10) return `${p.slice(0, 4)} ${p.slice(4, 7)} ${p.slice(7)}`;
  if (p.length === 12 && p.startsWith('254')) return `+${p.slice(0, 3)} ${p.slice(3, 6)} ${p.slice(6, 9)} ${p.slice(9)}`;
  return String(phone);
};

// Safe string renderer — prevents object-as-child errors
const safeStr = (val) => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'object') return String(val);
  return String(val);
};

// ═══════════════════════════════════════════════════════════
// Memoized Sub-components
// ═══════════════════════════════════════════════════════════
const SearchInput = React.memo(({ value, onChange, placeholder }) => (
  <div style={S.searchWrapper}>
    <span style={S.searchIcon}>🔍</span>
    <input
      type="text"
      placeholder={placeholder || 'Search by Name, ID, Phone, or Loan No...'}
      value={value}
      onChange={onChange}
      className="loan-detail-search-input"
      style={S.searchInput}
    />
  </div>
));

const InfoBanner = React.memo(() => (
  <div style={S.infoBanner}>
    <strong>📊 Dynamic Interest:</strong> Day 0 = disbursement (0%). Days 1–14 = 14% fixed.
    Days 15–30 = +1%/day (capped 30%). <strong>Penalties (3-tier):</strong> Day 31 = 5%, Day 45 = 10%, Day 60 = 15%
    of outstanding (P + I). <strong>"Remaining at Maturity"</strong> = original P+I minus payments.
  </div>
));

const ClosedLoansBanner = React.memo(() => (
  <div style={S.closedBanner}>
    <strong>🔒 Closed Loans Archive:</strong> Interest is <strong>permanently stopped</strong>.
    Values are <strong>frozen at closure</strong>. Use for completed-loan reporting.
  </div>
));

const TabButtons = React.memo(({ activeTab, onTabChange, tabs }) => {
  const tabList = tabs || TABS;
  const tabStyles = useMemo(() => tabList.map(tab => ({
    padding: '10px 0', border: 'none', background: 'none', fontWeight: '500', cursor: 'pointer',
    borderBottom: activeTab === tab.key ? '2px solid #3b82f6' : '2px solid transparent',
    color: activeTab === tab.key ? '#1e40af' : '#64748b',
  })), [activeTab, tabList]);

  return (
    <div className="flex gap-4 border-b" style={S.tabBorder}>
      {tabList.map((tab, i) => (
        <button key={tab.key} onClick={onTabChange} data-tab={tab.key} style={tabStyles[i]}>
          {tab.label}
        </button>
      ))}
    </div>
  );
});

const EmptyRow = React.memo(({ colSpan, message }) => (
  <tr><td colSpan={colSpan} className="text-center p-4 text-muted">{message}</td></tr>
));

const Pagination = React.memo(({ currentPage, totalPages, totalItems, onPageChange }) => {
  if (totalPages <= 1) return null;
  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [currentPage, totalPages]);

  return (
    <div className="flex justify-between items-center" style={{ padding: '12px 0', borderTop: '1px solid #e2e8f0', marginTop: 8 }}>
      <span className="text-sm text-muted">
        Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} of {totalItems}
      </span>
      <div className="flex gap-1 items-center">
        <button style={currentPage === 1 ? S.pageBtnDisabled : S.pageBtn} onClick={onPageChange} data-page={currentPage - 1} disabled={currentPage === 1}>‹</button>
        {pageNumbers.map(page => (
          <button key={page} style={page === currentPage ? S.pageBtnActive : S.pageBtn} onClick={onPageChange} data-page={page}>{page}</button>
        ))}
        <button style={currentPage === totalPages ? S.pageBtnDisabled : S.pageBtn} onClick={onPageChange} data-page={currentPage + 1} disabled={currentPage === totalPages}>›</button>
      </div>
    </div>
  );
});

const InterestRateTable = React.memo(({ principal, currentDay, isFrozen = false }) => (
  <table style={{ fontSize: 12, width: '100%', borderCollapse: 'collapse' }}>
    <thead>
      <tr style={V.rateTableHeaderBg}>
        <th style={{ ...V.rateCellLeft, textAlign: 'left' }}>Day #</th>
        <th style={V.rateCellCenter}>Rate</th>
        <th style={V.rateCellRight}>Interest</th>
        <th style={V.rateCellRight}>Total Repayable</th>
        <th style={V.rateCellCenter}>Notes</th>
      </tr>
    </thead>
    <tbody>
      {INTEREST_RATE_ROWS.map(row => {
        const isActive = currentDay === row.days;
        const isZero = row.days === 0;
        const interest = principal * (row.rate / 100);
        const rowStyle = isZero ? V.rateRowZero : (isActive ? V.rateRowActive : V.rateRowInactive);
        return (
          <tr key={row.days} style={rowStyle}>
            <td style={V.rateCellLeft}>{row.days}</td>
            <td style={V.rateCellCenter}>{row.rate}%</td>
            <td style={V.rateCellRight}>{formatCurrency(interest)}</td>
            <td style={V.rateCellRight}>{formatCurrency(principal + interest)}</td>
            <td style={V.rateCellNote}>{row.note}{isActive && (isFrozen ? ' ← FROZEN' : ' ← CURRENT')}</td>
          </tr>
        );
      })}
    </tbody>
  </table>
));

const PenaltyTierLegend = React.memo(() => (
  <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 11, color: '#64748b', marginTop: 4 }}>
    <span style={{ fontWeight: 600 }}>Penalty Tiers:</span>
    {PENALTY_TIERS.map(t => (
      <span key={t.tier} style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.color, display: 'inline-block' }}></span>
        <span>{t.label}: {t.percent > 0 ? `${t.percent}%` : '—'} ({t.days})</span>
      </span>
    ))}
  </div>
));

// ═══════════════════════════════════════════════════════════
// Active Loan List Row
// ═══════════════════════════════════════════════════════════
const LoanListRow = React.memo(({ loan, onClick }) => {
  const client = loan.client || {};
  const penaltyTier = loan.currentPenaltyTier || 0;
  const daysIntoDefault = loan.daysIntoDefault || 0;
  const daysSinceDisb = loan.daysSinceDisbursement || 0;
  const rate = loan.currentInterestPercent || 0;

  return (
    <tr onClick={onClick} data-loan-id={loan._id} style={V.cursorPointer}>
      <td style={V.loanNumCell}>{safeStr(loan.loanNumber)}</td>
      <td>
        <div style={{ fontWeight: 500, fontSize: 13 }}>{safeStr(client.firstName)} {safeStr(client.lastName)}</div>
        <div style={V.clientIdMono}>{safeStr(client.clientNumber)}</div>
      </td>
      <td style={V.phoneCell}>{formatPhone(client.phone)}</td>
      <td style={{ fontSize: 13 }}>{formatCurrency(loan.principalAmount || 0)}</td>
      <td>
        <div style={{ fontWeight: 600, color: '#f59e0b', fontSize: 13 }}>{rate}%</div>
        <div className="text-xs text-muted">Day {daysSinceDisb}</div>
      </td>
      <td style={{ fontSize: 13, fontWeight: 600 }}>{formatCurrency(loan.totalOutstanding || 0)}</td>
      <td>
        <span style={getPenaltyTierBadgeStyle(penaltyTier)}>
          {getPenaltyTierLabel(penaltyTier)}
        </span>
        {penaltyTier > 0 && (
          <div className="text-xs" style={{ color: '#dc2626', marginTop: 2 }}>
            {formatCurrency(loan.outstandingPenalty || 0)} · {daysIntoDefault}d
          </div>
        )}
      </td>
      <td>
        <span style={getStatusBadgeStyle(loan.status)}>
          {safeStr(loan.status).charAt(0).toUpperCase() + safeStr(loan.status).slice(1)}
        </span>
      </td>
      <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
        {loan.disbursementDate ? formatDate(loan.disbursementDate) : '—'}
      </td>
      <td><button className="btn btn-sm btn-outline">View</button></td>
    </tr>
  );
});

// ═══════════════════════════════════════════════════════════
// Closed Loans List Component
// ═══════════════════════════════════════════════════════════
const ClosedLoansList = React.memo(({ navigate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({ status: '', startDate: '', endDate: '' });

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchTerm); setCurrentPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page: currentPage, limit: ITEMS_PER_PAGE, sort: '-closedAt' });
        if (debouncedSearch) params.set('search', debouncedSearch);
        if (filters.status) params.set('status', filters.status);
        if (filters.startDate) params.set('startDate', filters.startDate);
        if (filters.endDate) params.set('endDate', filters.endDate);
        const res = await api.get(`/loans/closed?${params.toString()}`);
        if (!cancelled) setData(res.data.data);
      } catch (err) {
        if (!cancelled) console.error("Failed to fetch closed loans", err);
      } finally { if (!cancelled) setLoading(false); }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [currentPage, debouncedSearch, filters]);

  const handleSearchChange = useCallback((e) => setSearchTerm(e.target.value), []);
  const handlePageChange = useCallback((e) => {
    const p = parseInt(e.currentTarget.dataset.page, 10);
    if (p && p >= 1) setCurrentPage(p);
  }, []);
  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const handleExport = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (filters.status) params.set('status', filters.status);
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      const res = await api.get(`/loans/closed/report?${params.toString()}`);
      const loans = res.data.data.loans || [];
      if (loans.length === 0) { alert('No data to export'); return; }
      const headers = ['Loan No', 'Client', 'Phone', 'Client ID', 'Branch', 'Principal', 'Disbursed', 'Total Interest', 'Total Paid', 'Outstanding', 'Penalty', 'Days Used', 'Final Rate', 'Penalty Tier', 'Closed At', 'Closed By', 'Reason', 'Status'];
      const rows = loans.map(l => [
        l.loanNumber, l.clientName, l.clientPhone || '', l.clientNumber, l.branchName,
        l.principalAmount, l.disbursementAmount, l.totalInterest, l.totalPaid,
        l.totalOutstanding, l.outstandingPenalty || 0, l.actualDaysUsed,
        l.finalInterestRate ? `${l.finalInterestRate}%` : '',
        l.currentPenaltyTier > 0 ? `Tier ${l.currentPenaltyTier}` : 'None',
        l.closedAt ? new Date(l.closedAt).toLocaleDateString() : '',
        l.closedByName, l.closureReason || '', l.status
      ]);
      const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell || ''}"`).join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `closed-loans-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export failed: ' + (err.response?.data?.message || err.message));
    }
  }, [debouncedSearch, filters]);

  const loans = data?.loans || [];
  const pagination = data?.pagination || { page: 1, pages: 1, total: 0 };
  const stats = data?.stats;

  const handleLoanClick = useCallback((e) => {
    const row = e.currentTarget.closest('tr[data-loan-id]');
    if (row) navigate(`/loans/${row.dataset.loanId}`);
  }, [navigate]);

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-3">
          <h2 style={{ margin: 0, color: '#065f46' }}>🔒 Closed Loans</h2>
          <SearchInput value={searchTerm} onChange={handleSearchChange} placeholder="Search closed loans..." />
        </div>
        <div className="flex gap-2">
          <button style={S.exportBtn} onClick={handleExport}>📥 Export CSV</button>
          <button className="btn btn-outline" onClick={() => navigate('/loans/active')}>← Active Loans</button>
        </div>
      </div>
      <ClosedLoansBanner />

      <div style={S.filterRow}>
        <span style={S.filterLabel}>Filters:</span>
        <select style={S.filterSelect} value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)}>
          <option value="">All Statuses</option>
          <option value="closed">Closed</option>
          <option value="written-off">Written Off</option>
        </select>
        <span style={S.filterLabel}>Closed From:</span>
        <input type="date" style={S.dateInput} value={filters.startDate} onChange={(e) => handleFilterChange('startDate', e.target.value)} />
        <span style={S.filterLabel}>To:</span>
        <input type="date" style={S.dateInput} value={filters.endDate} onChange={(e) => handleFilterChange('endDate', e.target.value)} />
        {(filters.status || filters.startDate || filters.endDate) && (
          <button style={S.clearBtn} onClick={() => setFilters({ status: '', startDate: '', endDate: '' })}>✕ Clear</button>
        )}
      </div>

      {stats && (
        <div className="grid grid-5 gap-2 mb-2">
          <div style={S.statsCard}>
            <div className="text-xs text-muted">Total Closed</div>
            <div style={V.statValueGreen}>{safeStr(stats.totalClosed)}</div>
          </div>
          <div style={S.statsCard}>
            <div className="text-xs text-muted">Total Disbursed</div>
            <div style={V.statValueBlue}>{formatCurrency(stats.totalPrincipalDisbursed || 0)}</div>
          </div>
          <div style={S.statsCard}>
            <div className="text-xs text-muted">Total Collected</div>
            <div style={V.statValueGreen}>{formatCurrency(stats.totalAmountCollected || 0)}</div>
          </div>
          <div style={S.statsCard}>
            <div className="text-xs text-muted">Avg Days</div>
            <div style={V.statValuePurple}>{safeStr(stats.avgDaysToClose)}d</div>
          </div>
          <div style={S.statsCard}>
            <div className="text-xs text-muted">Collection Rate</div>
            <div style={{
              ...V.statValueGreen,
              color: (stats.collectionRate || 0) >= 90 ? '#059669' : (stats.collectionRate || 0) >= 70 ? '#d97706' : '#dc2626'
            }}>{safeStr(stats.collectionRate)}%</div>
            <div className="text-xs text-muted">{safeStr(stats.fullyPaidCount)} full · {safeStr(stats.partiallyPaidCount)} partial</div>
          </div>
        </div>
      )}

      <div className="card">
        {loading ? <div className="spinner"></div> : (
          <>
            <div className="table-container">
              <table>
                <thead>
                  <tr style={S.closedListHeader}>
                    <th>Loan No</th><th>Client</th><th>Phone</th><th>Principal</th>
                    <th>Total Paid</th><th>Outstanding</th><th>Penalty</th><th>Days Used</th>
                    <th>Final Rate</th><th>Closed At</th><th>Reason</th><th>Status</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {loans.length === 0 ? (
                    <EmptyRow colSpan={13} message={debouncedSearch ? 'No matches found.' : 'No closed loans.'} />
                  ) : loans.map(loan => {
                    const cl = loan.client || {};
                    const outAmt = loan.totalOutstanding || 0;
                    return (
                      <tr key={loan._id} onClick={handleLoanClick} data-loan-id={loan._id} style={V.cursorPointer}>
                        <td style={V.loanNumCell}>{safeStr(loan.loanNumber)}</td>
                        <td>
                          <div style={{ fontWeight: 500 }}>{safeStr(cl.firstName)} {safeStr(cl.lastName)}</div>
                          <div className="text-xs text-muted" style={V.clientIdMono}>{safeStr(cl.clientNumber)}</div>
                        </td>
                        <td style={V.phoneCell}>{formatPhone(cl.phone)}</td>
                        <td>{formatCurrency(loan.principalAmount || 0)}</td>
                        <td style={V.owedGreen}>{formatCurrency(loan.totalPaid || 0)}</td>
                        <td style={outAmt <= 0 ? V.owedGreen : V.owedRed}>
                          {formatCurrency(outAmt)}
                          {outAmt <= 0 && <div className="text-xs" style={{ color: '#059669' }}>✓ Paid</div>}
                        </td>
                        <td>
                          {(loan.outstandingPenalty || 0) > 0
                            ? <span style={{ color: '#dc2626', fontWeight: 600 }}>{formatCurrency(loan.outstandingPenalty)}</span>
                            : <span style={{ color: '#94a3b8' }}>—</span>}
                        </td>
                        <td>
                          <div style={V.daysBold}>{safeStr(loan.actualDaysUsed || 0)}d</div>
                          <div className="text-xs text-muted">of {safeStr(loan.duration)}d</div>
                        </td>
                        <td style={V.rateAmber}>{loan.finalInterestRate ? `${loan.finalInterestRate}%` : '—'}</td>
                        <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                          {loan.closedAt ? formatDate(loan.closedAt) : '—'}
                        </td>
                        <td style={{ fontSize: 12, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={safeStr(loan.closureReason)}>
                          {safeStr(loan.closureReason) || '—'}
                        </td>
                        <td>
                          <span style={loan.status === 'written-off' ? V.writtenOffBadge : V.closedStatusBadge}>
                            {loan.status === 'written-off' ? 'WRITTEN OFF' : 'CLOSED'}
                          </span>
                        </td>
                        <td><button className="btn btn-sm btn-outline">View</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination currentPage={pagination.page} totalPages={pagination.pages} totalItems={pagination.total} onPageChange={handlePageChange} />
          </>
        )}
      </div>
    </div>
  );
});

// ═══════════════════════════════════════════════════════════
// Closure Details Tab
// ═══════════════════════════════════════════════════════════
const ClosureDetailsTab = React.memo(({ loan }) => {
  const isWrittenOff = loan.status === 'written-off';
  const closedBy = loan.closedBy || {};
  return (
    <div className="p-3">
      <div className="card p-3 mb-3" style={S.frozenBox}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <span style={{ fontSize: 20 }}>🔒</span>
          <strong style={{ fontSize: 14, color: '#0284c7' }}>
            {isWrittenOff ? 'WRITTEN OFF' : 'LOAN CLOSED'} — Interest Permanently Stopped
          </strong>
        </div>
        <div className="text-xs" style={{ color: '#0c4a6e', lineHeight: 1.6 }}>
          Closed on {formatDateTime(loan.closedAt)}. All calculations <strong>frozen</strong> at the values below.
        </div>
      </div>
      <h4 className="mb-2">Closure Information</h4>
      <div className="table-container">
        <table style={{ fontSize: '13px' }}>
          <thead>
            <tr style={V.tableHeaderBg}>
              <th style={S.tc}>Field</th><th style={S.tcr}>Value</th><th style={S.tc}>Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr style={V.closureRow}><td style={V.closureLabel}>Final Status</td><td style={V.closureValue}><span style={isWrittenOff ? V.writtenOffBadge : V.closedStatusBadge}>{isWrittenOff ? 'WRITTEN OFF' : 'CLOSED'}</span></td><td style={V.tcNote}>Terminal status</td></tr>
            <tr style={V.closureRow}><td style={V.closureLabel}>Closure Date</td><td style={V.closureValue}>{formatDateTime(loan.closedAt)}</td><td style={V.tcNote}>Interest stopped</td></tr>
            <tr style={V.closureRow}><td style={V.closureLabel}>Closed By</td><td style={V.closureValue}>{safeStr(closedBy.firstName)} {safeStr(closedBy.lastName)} ({safeStr(closedBy.email)})</td><td style={V.tcNote}>User who closed</td></tr>
            <tr style={V.closureRow}><td style={V.closureLabel}>Reason</td><td style={V.closureValue}>{safeStr(loan.closureReason) || '—'}</td><td style={V.tcNote}>Provided at closure</td></tr>
            <tr style={V.closureRow}><td style={V.closureLabel}>Actual Days Used</td><td style={V.closureValue}>{loan.actualDaysUsed != null ? safeStr(loan.actualDaysUsed) : '—'} days</td><td style={V.tcNote}>Day 0 = disbursement</td></tr>
            <tr style={V.closureRow}><td style={V.closureLabel}>Final Interest Rate</td><td style={{ ...V.closureValue, color: '#f59e0b' }}>{loan.finalInterestRate ? `${loan.finalInterestRate}%` : '—'}</td><td style={V.tcNote}>Tiered rate at closure</td></tr>
            <tr style={V.closureRow}><td style={V.closureLabel}>Final Interest</td><td style={{ ...V.closureValue, color: '#f59e0b' }}>{formatCurrency(loan.outstandingInterest || 0)}</td><td style={V.tcNote}>Frozen at closure</td></tr>
            <tr style={V.closureRow}><td style={V.closureLabel}>Final Penalty</td><td style={{ ...V.closureValue, color: (loan.outstandingPenalty || 0) > 0 ? '#dc2626' : '#059669' }}>{formatCurrency(loan.outstandingPenalty || 0)}</td><td style={V.tcNote}>{loan.currentPenaltyTier > 0 ? `Tier ${loan.currentPenaltyTier} (${loan.daysIntoDefault || 0}d into default)` : 'No penalty'}</td></tr>
            <tr style={V.closureRow}><td style={V.closureLabel}>Total Outstanding</td><td style={{ ...V.closureValue, fontWeight: 700, fontSize: '15px' }}>{formatCurrency(loan.totalOutstanding || 0)}</td><td style={V.tcNote}>P + I + Penalties</td></tr>
          </tbody>
        </table>
      </div>
      <h4 className="mb-2 mt-4">Loan Timeline</h4>
      <div style={{ paddingLeft: 20, borderLeft: '3px solid #e2e8f0' }}>
        <div style={{ marginBottom: 18, position: 'relative', paddingLeft: 20 }}>
          <div style={{ position: 'absolute', left: -28, top: 0, width: 12, height: 12, borderRadius: '50%', background: '#3b82f6', border: '2px solid #fff' }}></div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1e40af' }}>Disbursement — Day 0</div>
          <div className="text-xs text-muted">{formatDateTime(loan.disbursementDate)} · {formatCurrency(loan.disbursementAmount || 0)}</div>
        </div>
        <div style={{ marginBottom: 18, position: 'relative', paddingLeft: 20 }}>
          <div style={{ position: 'absolute', left: -28, top: 0, width: 12, height: 12, borderRadius: '50%', background: '#f59e0b', border: '2px solid #fff' }}></div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e' }}>Maturity — Day {safeStr(loan.duration)}</div>
          <div className="text-xs text-muted">{formatDate(loan.maturityDate)} · Expected: {formatCurrency(loan.totalRepayable || 0)}</div>
        </div>
        {(loan.actualDaysUsed || 0) > (loan.duration || 0) && (
          <div style={{ marginBottom: 18, position: 'relative', paddingLeft: 20 }}>
            <div style={{ position: 'absolute', left: -28, top: 0, width: 12, height: 12, borderRadius: '50%', background: '#dc2626', border: '2px solid #fff' }}></div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#991b1b' }}>Default Phase — Day 31+</div>
            <div className="text-xs text-muted">{(loan.actualDaysUsed || 0) - (loan.duration || 0)} days past maturity</div>
          </div>
        )}
        <div style={{ position: 'relative', paddingLeft: 20 }}>
          <div style={{ position: 'absolute', left: -28, top: 0, width: 12, height: 12, borderRadius: '50%', background: isWrittenOff ? '#dc2626' : '#059669', border: '2px solid #fff' }}></div>
          <div style={{ fontSize: 13, fontWeight: 600, color: isWrittenOff ? '#991b1b' : '#065f46' }}>{isWrittenOff ? 'Written Off' : 'Closed'} — Day {safeStr(loan.actualDaysUsed)}</div>
          <div className="text-xs text-muted">{formatDateTime(loan.closedAt)} · Rate: {safeStr(loan.finalInterestRate)}% · Outstanding: {formatCurrency(loan.totalOutstanding || 0)}</div>
        </div>
      </div>
    </div>
  );
});

const FrozenInterestSummaryCard = React.memo(({ interestSummary, loan }) => {
  const isWrittenOff = loan.status === 'written-off';
  const s = interestSummary || {};
  return (
    <div className="card mb-2" style={S.closedLoanBorder}>
      <div style={S.closedLoanHeader}>
        <strong style={{ fontSize: 14, color: isWrittenOff ? '#991b1b' : '#065f46' }}>
          {isWrittenOff ? '🗑️ WRITTEN OFF' : '🔒 CLOSED'} — Interest FROZEN
        </strong>
      </div>
      <div className="grid grid-4" style={V.summaryGrid}>
        <div className="text-center">
          <div className="text-xs text-muted">Days Used</div>
          <div className="font-bold text-lg" style={V.frozenValue}>{safeStr(s.daysSinceDisbursement)}</div>
          <div className="text-xs text-muted">of {safeStr(s.totalLoanDays)}d</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-muted">Final Rate</div>
          <div className="font-bold text-lg" style={V.rateAmberLg}>{safeStr(s.currentInterestPercent)}%</div>
          <div className="text-xs text-muted">Locked</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-muted">Final Interest</div>
          <div className="font-bold text-lg" style={V.rateAmberLg}>{formatCurrency(s.accruedInterest || 0)}</div>
          <div className="text-xs text-muted">No accrual</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-muted">Total Outstanding</div>
          <div className="font-bold text-lg" style={{ color: (s.totalAmountOwed || 0) > 0 ? '#dc2626' : '#059669' }}>
            {formatCurrency(s.totalAmountOwed || 0)}
          </div>
          <div className="text-xs text-muted">{(s.totalAmountOwed || 0) <= 0 ? '✓ Settled' : 'Still owed'}</div>
        </div>
      </div>
    </div>
  );
});

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════
export default function LoanDetail({ isList = false, isClosedList = false }) {
  const { id } = useParams();
  const navigate = useNavigate();

  // ═══════════════════════════════════════════════════════════
  // ALL useState calls MUST be here — before any conditional returns
  // (React Rules of Hooks)
  // ═══════════════════════════════════════════════════════════
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('schedule');
  const [recalculating, setRecalculating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({ status: '', startDate: '', endDate: '' });
  const [ihPage, setIhPage] = useState(1);
  const [payPage, setPayPage] = useState(1);
  const [interactionModalOpen, setInteractionModalOpen] = useState(false);
  const [interactionType, setInteractionType] = useState('other');
  const [interactionContent, setInteractionContent] = useState('');
  const [savingInteraction, setSavingInteraction] = useState(false);

  // Data Fetching
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        if (isList) {
          const params = new URLSearchParams({ page: currentPage, limit: ITEMS_PER_PAGE });
          if (debouncedSearch) params.set('search', debouncedSearch);
          if (filters.status) params.set('status', filters.status);
          if (filters.startDate) params.set('startDate', filters.startDate);
          if (filters.endDate) params.set('endDate', filters.endDate);
          const res = await api.get(`/loans?${params.toString()}`);
          if (!cancelled) setData(res.data.data);
        } else if (!isClosedList) {
          const res = await api.get(`/loans/${id}`);
          if (!cancelled) setData(res.data.data);
        }
      } catch (err) {
        if (!cancelled) console.error("Failed to fetch loan data", err);
      } finally { if (!cancelled) setLoading(false); }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [id, isList, isClosedList, currentPage, debouncedSearch, filters]);

  // Debounce Search
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchTerm); setCurrentPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Handlers
  const openInteractionModal = useCallback(() => {
    const currentLoan = data || {};
    setInteractionType('other');
    setInteractionContent(`Interaction regarding loan ${currentLoan.loanNumber || id}`);
    setInteractionModalOpen(true);
  }, [data, id]);

  const handleSaveInteraction = useCallback(async () => {
    const currentLoan = data || {};
    const clientId = currentLoan.client?._id || currentLoan.client?.id;
    if (!clientId || !interactionContent.trim()) {
      alert(!clientId ? 'Unable to identify the client for this loan.' : 'Please enter interaction details.');
      return;
    }
    setSavingInteraction(true);
    try {
      await api.post('/communications', {
        client: clientId,
        conversationMethod: interactionType,
        conversationPurpose: `Loan ${currentLoan.loanNumber || id}`,
        conversationOutcome: 'Contactable',
        content: interactionContent.trim(),
        nextSteps: 'Follow up as needed',
      });
      setInteractionModalOpen(false);
      setInteractionContent('');
      alert('Interaction recorded successfully.');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to record interaction.');
    } finally { setSavingInteraction(false); }
  }, [data, id, interactionContent, interactionType]);

  const handleRecalculateInterest = useCallback(async () => {
    if (!confirm('Recalculate interest for all active loans?')) return;
    setRecalculating(true);
    try {
      await api.post('/loans/recalculate-interest');
      alert('Interest recalculated!');
      const params = new URLSearchParams({ page: currentPage, limit: ITEMS_PER_PAGE });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (filters.status) params.set('status', filters.status);
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      const res = await api.get(`/loans?${params.toString()}`);
      setData(res.data.data);
    } catch (err) { alert(err.response?.data?.message || 'Failed'); }
    finally { setRecalculating(false); }
  }, [currentPage, debouncedSearch, filters]);

  const handleSearchChange = useCallback((e) => setSearchTerm(e.target.value), []);
  const handleTabChange = useCallback((e) => {
    const tab = e.currentTarget.dataset.tab;
    if (tab) setActiveTab(tab);
  }, []);
  const handleLoanClick = useCallback((e) => {
    const row = e.currentTarget.closest('tr[data-loan-id]');
    if (row) navigate(`/loans/${row.dataset.loanId}`);
  }, [navigate]);
  const handlePageChange = useCallback((e) => {
    const p = parseInt(e.currentTarget.dataset.page, 10);
    if (p && p >= 1) setCurrentPage(p);
  }, []);
  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);
  const handleClearFilters = useCallback(() => {
    setFilters({ status: '', startDate: '', endDate: '' });
    setCurrentPage(1);
  }, []);

  const handleExportActive = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (filters.status) params.set('status', filters.status);
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      const res = await api.get(`/loans/active/report?${params.toString()}`);
      const loans = res.data.data.loans || [];
      if (loans.length === 0) { alert('No data to export'); return; }
      const headers = ['Loan No', 'Client', 'Phone', 'Client ID', 'Branch', 'Principal', 'Disbursed', 'Amount Paid', 'Outstanding', 'Interest', 'Penalty', 'Penalty Tier', 'Days', 'Rate', 'Status', 'Disbursed Date'];
      const rows = loans.map(l => [
        l.loanNumber, l.clientName, l.clientPhone || '', l.clientNumber, l.branchName,
        l.principalAmount, l.disbursementAmount, l.totalPaid || l.amountPaid || l.paidAmount || 0, l.totalOutstanding, l.outstandingInterest,
        l.outstandingPenalty || 0, l.currentPenaltyTier > 0 ? `Tier ${l.currentPenaltyTier}` : 'None',
        l.daysSinceDisbursement, `${l.currentInterestPercent}%`, l.status,
        l.disbursementDate ? new Date(l.disbursementDate).toLocaleDateString() : ''
      ]);
      const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell || ''}"`).join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `active-loans-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export failed: ' + (err.response?.data?.message || err.message));
    }
  }, [debouncedSearch, filters]);

  const isClosedLoan = data?.status === 'closed' || data?.status === 'written-off';

  // Computed Loan Values
  const loanComputed = useMemo(() => {
    if (isList || isClosedList || !data || !data._id) return null;
    const loan = data;
    const summary = loan.interestSummary || {};
    const activeBalance = (summary.outstandingPrincipal || loan.outstandingPrincipal || 0) +
                          (summary.outstandingInterest || loan.outstandingInterest || 0);
    const totalAmountOwed = summary.totalAmountOwed ?? loan.totalOutstanding ?? 0;
    const remainingAtMaturity = loan.remainingAtMaturity ?? Math.max(0, (loan.totalRepayable || 0) - (loan.totalPaid || 0));

    const getRateDescription = () => {
      if (!summary) return '';
      if (summary.isClosed) return `FROZEN — ${safeStr(summary.currentInterestPercent)}% for ${safeStr(summary.daysSinceDisbursement)} days`;
      const d = summary.daysSinceDisbursement;
      const r = summary.currentInterestPercent;
      if (d === 0) return 'Disbursement day (0%)';
      if (d <= 14) return 'Base rate (14%)';
      if (r >= 30) return 'Maximum cap (30%)';
      return `Tiered: Day ${d} = ${r}%`;
    };

    const getPenaltyTierDescription = () => {
      if (!summary || !summary.isOverdue) {
        if (summary?.isClosed) return 'No penalty — closed';
        return 'No penalty — within term';
      }
      const d = summary.daysOverdue || summary.daysIntoDefault || 0;
      const tier1Max = summary.tier1MaxDays || 14;
      const tier2Max = summary.tier2MaxDays || 29;
      if (d <= tier1Max) return `Tier 1: ${summary.tier1Percent ?? 5}% (Day 31–44)`;
      if (d <= tier2Max) return `Tier 2: ${summary.tier2Percent ?? 10}% (Day 45–59)`;
      return `Tier 3: ${summary.tier3Percent ?? 15}% (Day 60+)`;
    };

    return {
      loan, client: loan.client, loanProduct: loan.loanProduct, repayments: loan.repayments,
      repaymentSchedule: loan.repaymentSchedule, interestHistory: loan.interestHistory,
      interestSummary: loan.interestSummary, summary, activeBalance, totalAmountOwed,
      remainingAtMaturity, rateDescription: getRateDescription(),
      penaltyTierDescription: getPenaltyTierDescription(), isClosedLoan,
    };
  }, [data, isList, isClosedList, isClosedLoan]);

  // ═══════════════════════════════════════════════════════════
  // VIEW 0: CLOSED LOANS LIST
  // ═══════════════════════════════════════════════════════════
  if (isClosedList) {
    return <ClosedLoansList navigate={navigate} />;
  }

  // ═══════════════════════════════════════════════════════════
  // VIEW 1: ACTIVE LOANS LIST
  // ═══════════════════════════════════════════════════════════
  if (isList) {
    const loans = data?.loans || [];
    const pagination = data?.pagination || { page: 1, pages: 1, total: 0 };
    const stats = data?.stats;
    const hasActiveFilters = filters.status || filters.startDate || filters.endDate;

    return (
      <div>
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-3">
            <h2 style={{ margin: 0 }}>Active Loans</h2>
            <SearchInput value={searchTerm} onChange={handleSearchChange} />
          </div>
          <div className="flex gap-2">
            <button style={S.exportBtn} onClick={handleExportActive}>📥 Export CSV</button>
            <button className="btn btn-sm" style={{ background: '#059669', color: '#fff', border: 'none' }} onClick={() => navigate('/loans/closed')}>🔒 Closed Loans</button>
            <button className="btn btn-warning btn-sm" onClick={handleRecalculateInterest} disabled={recalculating}>
              {recalculating ? '⏳ ...' : '🔄 Recalculate'}
            </button>
            <button className="btn btn-outline" onClick={() => navigate('/loans/applications')}>← Applications</button>
          </div>
        </div>

        <InfoBanner />

        <div style={S.filterRow}>
          <span style={S.filterLabel}>Filters:</span>
          <select style={S.filterSelect} value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)}>
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="overdue">Overdue</option>
            <option value="defaulted">Defaulted</option>
          </select>
          <span style={S.filterLabel}>Disbursed From:</span>
          <input type="date" style={S.dateInput} value={filters.startDate} onChange={(e) => handleFilterChange('startDate', e.target.value)} />
          <span style={S.filterLabel}>To:</span>
          <input type="date" style={S.dateInput} value={filters.endDate} onChange={(e) => handleFilterChange('endDate', e.target.value)} />
          {hasActiveFilters && (
            <button style={S.clearBtn} onClick={handleClearFilters}>✕ Clear Filters</button>
          )}
        </div>

        {stats && (
          <div className="grid grid-5 gap-2 mb-2">
            <div style={S.statsCard}>
              <div className="text-xs text-muted">Total Loans</div>
              <div style={V.statValueBlue}>{safeStr(stats.totalActive)}</div>
              <div className="text-xs text-muted">{safeStr(stats.activeCount)} active · {safeStr(stats.overdueCount)} overdue · {safeStr(stats.defaultedCount)} defaulted</div>
            </div>
            <div style={S.statsCard}>
              <div className="text-xs text-muted">Total Disbursed</div>
              <div style={V.statValueBlue}>{formatCurrency(stats.totalPrincipalDisbursed || 0)}</div>
            </div>
            <div style={S.statsCard}>
              <div className="text-xs text-muted">Total Outstanding</div>
              <div style={V.statValueRed}>{formatCurrency(stats.totalOutstanding || 0)}</div>
              <div className="text-xs text-muted">Collected: {formatCurrency(stats.totalCollected || 0)}</div>
            </div>
            <div style={S.statsCard}>
              <div className="text-xs text-muted">Penalty Outstanding</div>
              <div style={V.statValueAmber}>{formatCurrency(stats.totalPenaltyOutstanding || 0)}</div>
              <div className="text-xs text-muted">
                {safeStr(stats.loansWithPenalties || 0)} loans with penalties
                {(stats.tier1Count || 0) > 0 && <span style={{ color: '#92400e' }}> · T1:{safeStr(stats.tier1Count)}</span>}
                {(stats.tier2Count || 0) > 0 && <span style={{ color: '#9a3412' }}> · T2:{safeStr(stats.tier2Count)}</span>}
                {(stats.tier3Count || 0) > 0 && <span style={{ color: '#991b1b' }}> · T3:{safeStr(stats.tier3Count)}</span>}
              </div>
            </div>
            <div style={S.statsCard}>
              <div className="text-xs text-muted">Avg Days</div>
              <div style={V.statValuePurple}>{safeStr(stats.avgDays)}d</div>
            </div>
          </div>
        )}

        <PenaltyTierLegend />

        <div className="card" style={{ marginTop: 8 }}>
          {loading ? (
            <div className="spinner"></div>
          ) : (
            <>
              <div className="table-container">
                <table>
                  <thead>
                    <tr style={S.activeListHeader}>
                      <th>Loan No</th><th>Client</th><th>Phone</th><th>Principal</th>
                      <th>Rate / Day</th><th>Outstanding</th><th>Penalty Tier</th>
                      <th>Status</th><th>Disbursed</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loans.length === 0 ? (
                      <EmptyRow colSpan={10} message={debouncedSearch || hasActiveFilters ? 'No matching loans found.' : 'No active loans.'} />
                    ) : loans.map(loan => (
                      <LoanListRow key={loan._id} loan={loan} onClick={handleLoanClick} />
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination currentPage={pagination.page} totalPages={pagination.pages} totalItems={pagination.total} onPageChange={handlePageChange} />
            </>
          )}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // VIEW 2: SINGLE LOAN DETAIL
  // ═══════════════════════════════════════════════════════════
  if (loading) return <div className="spinner"></div>;
  if (!data || !data._id) return <div className="text-center p-4 text-muted">Loan not found.</div>;

  const c = loanComputed;
  if (!c) return <div className="text-center p-4 text-muted">Error loading loan data.</div>;

  const { loan, client, loanProduct, repayments, repaymentSchedule, interestHistory, summary, activeBalance, totalAmountOwed, remainingAtMaturity } = c;
  const isClosed = c.isClosedLoan;
  const tabs = isClosed ? CLOSED_LOAN_TABS : TABS;
  const cl = client || {};
  const lp = loanProduct || {};
  const br = loan.branch || {};
  const ao = loan.assignedOfficer || {};

  // Interest History Pagination
  const ihTotal = (interestHistory || []).length;
  const ihPages = Math.ceil(ihTotal / 30);
  const ihSlice = (interestHistory || []).slice((ihPage - 1) * 30, ihPage * 30);

  // Payment History Pagination
  const payTotal = (repayments || []).length;
  const payPages = Math.ceil(payTotal / 20);
  const paySlice = (repayments || []).slice((payPage - 1) * 20, payPage * 20);

  return (
    <div>
      {/* HEADER */}
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-3">
          <button className="btn btn-outline btn-sm" onClick={() => navigate(isClosed ? '/loans/closed' : '/loans/active')}>← Back</button>
          <span style={S.loanNumber}>{safeStr(loan.loanNumber)}</span>
          <span style={{
            padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
            background: getLoanStatusColor(loan.status) + '20',
            color: getLoanStatusColor(loan.status),
          }}>
            {safeStr(loan.status).charAt(0).toUpperCase() + safeStr(loan.status).slice(1)}
          </span>
          {isClosed && <span style={{ color: '#059669', fontWeight: 600, fontSize: 13 }}>🔒 FROZEN</span>}
        </div>
        <div className="flex items-center gap-2">
          {cl.phone && <span style={{ fontSize: 13, color: '#475569' }}>📞 {formatPhone(cl.phone)}</span>}
        </div>
      </div>

      {/* Client Info Bar */}
      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 16px', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <strong style={{ fontSize: 14 }}>{safeStr(cl.firstName)} {safeStr(cl.lastName)}</strong>
          <span style={{ ...V.clientIdMono, marginLeft: 12, color: '#64748b' }}>{safeStr(cl.clientNumber)}</span>
          {cl.phone && <span style={{ ...V.phoneCell, marginLeft: 12 }}>📞 {formatPhone(cl.phone)}</span>}
        </div>
        <div className="text-xs text-muted">
          {safeStr(lp.name)} · {safeStr(loan.duration)}{safeStr(loan.durationUnit)?.charAt(0)} · Disbursed: {formatDate(loan.disbursementDate)} · Maturity: {formatDate(loan.maturityDate)}
        </div>
      </div>

      {/* Interest Summary Card */}
      {isClosed ? (
        <FrozenInterestSummaryCard interestSummary={summary} loan={loan} />
      ) : (
        <div className="card mb-2" style={{
          borderRadius: 10, overflow: 'hidden',
          border: `2px solid ${(summary?.isOverdue || false) ? '#ef4444' : '#10b981'}`
        }}>
          <div style={(summary?.isOverdue || false) ? S.overdueLoanHeader : S.activeLoanHeader}>
            <strong style={{ fontSize: 14, color: (summary?.isOverdue || false) ? '#991b1b' : '#065f46' }}>
              {(summary?.isOverdue || false) ? '⚠️ OVERDUE' : '✅ ACTIVE'} — Day {safeStr(summary?.daysSinceDisbursement || 0)} of {safeStr(summary?.totalLoanDays || loan.duration)}
            </strong>
            <span style={{ marginLeft: 12, fontSize: 12, color: '#64748b' }}>{c.rateDescription}</span>
          </div>
          <div className="grid grid-5" style={V.summaryGrid}>
            <div className="text-center">
              <div className="text-xs text-muted">Principal</div>
              <div className="font-bold text-lg">{formatCurrency(loan.principalAmount || 0)}</div>
              <div className="text-xs text-muted">Owed: {formatCurrency(summary?.outstandingPrincipal || 0)}</div>
            </div>
            <div className="text-center">
              <div className="text-xs text-muted">Interest ({safeStr(summary?.currentInterestPercent || 0)}%)</div>
              <div className="font-bold text-lg" style={V.rateAmberLg}>{formatCurrency(summary?.accruedInterest || 0)}</div>
              <div className="text-xs text-muted">Owed: {formatCurrency(summary?.outstandingInterest || 0)}</div>
            </div>
            <div className="text-center">
              <div className="text-xs text-muted">Penalty</div>
              <div className="font-bold text-lg" style={(summary?.accruedPenalty || 0) > 0 ? V.penaltyRedLg : V.penaltyGrayLg}>
                {formatCurrency(summary?.accruedPenalty || 0)}
              </div>
              <div className="text-xs text-muted" style={{ fontSize: 11 }}>{c.penaltyTierDescription}</div>
            </div>
            <div className="text-center">
              <div className="text-xs text-muted">Remaining at Maturity</div>
              <div className="font-bold text-lg" style={{ color: '#7c3aed' }}>{formatCurrency(remainingAtMaturity)}</div>
              <div style={V.progressBarBg}>
                <div style={{ ...V.progressBarFill, width: `${Math.min(100, ((loan.totalPaid || 0) / (loan.totalRepayable || 1)) * 100)}%` }}></div>
              </div>
              <div style={V.progressLabel} className="text-xs">{Math.round(((loan.totalPaid || 0) / (loan.totalRepayable || 1)) * 100)}% paid</div>
            </div>
            <div className="text-center">
              <div className="text-xs text-muted">Total Owed Now</div>
              <div className="font-bold text-lg" style={{ color: totalAmountOwed > 0 ? '#dc2626' : '#059669' }}>
                {formatCurrency(totalAmountOwed)}
              </div>
              <div className="text-xs text-muted">P + I + Penalties</div>
            </div>
          </div>
        </div>
      )}

      <TabButtons activeTab={activeTab} onTabChange={handleTabChange} tabs={tabs} />

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* TAB: LOAN INFO */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {activeTab === 'info' && (
        <div className="p-3">
          <div className="grid grid-2 gap-3 mb-3">
            <div className="card p-3">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}><h4 className="mb-2" style={{ fontSize: 14 }}>📋 Loan Details</h4><button type="button" className="btn btn-primary btn-sm" onClick={openInteractionModal}>💬 Log Interaction</button></div>
              <table style={{ fontSize: 13, width: '100%' }}>
                <tbody>
                  <tr><td style={S.tc} className="text-muted">Loan Number</td><td style={S.tcr}><span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{safeStr(loan.loanNumber)}</span></td></tr>
                  <tr><td style={S.tc} className="text-muted">Product</td><td style={S.tcr}>{safeStr(lp.name)} ({safeStr(lp.productCode)})</td></tr>
                  <tr><td style={S.tc} className="text-muted">Branch</td><td style={S.tcr}>{safeStr(br.name)}</td></tr>
                  <tr><td style={S.tc} className="text-muted">Duration</td><td style={S.tcr}>{safeStr(loan.duration)} {safeStr(loan.durationUnit)}{Number(loan.duration) > 1 ? 's' : ''}</td></tr>
                  <tr><td style={S.tc} className="text-muted">Repayment</td><td style={S.tcr}>{safeStr(loan.repaymentFrequency)}</td></tr>
                  <tr><td style={S.tc} className="text-muted">Officer</td><td style={S.tcr}>{safeStr(ao.firstName)} {safeStr(ao.lastName)}</td></tr>
                </tbody>
              </table>
            </div>
            <div className="card p-3">
              <h4 className="mb-2" style={{ fontSize: 14 }}>💰 Financial Summary</h4>
              <table style={{ fontSize: 13, width: '100%' }}>
                <tbody>
                  <tr><td style={S.tc} className="text-muted">Principal</td><td style={S.tcr}>{formatCurrency(loan.principalAmount)}</td></tr>
                  <tr><td style={S.tc} className="text-muted">Processing Fee</td><td style={S.tcr}>{formatCurrency(loan.processingFee || 0)}</td></tr>
                  <tr><td style={S.tc} className="text-muted">Insurance Fee</td><td style={S.tcr}>{formatCurrency(loan.insuranceFee || 0)}</td></tr>
                  <tr style={{ background: '#f8fafc' }}><td style={{ ...S.tc, fontWeight: 600 }}>Disbursed to Client</td><td style={{ ...S.tcr, fontWeight: 600 }}>{formatCurrency(loan.disbursementAmount || 0)}</td></tr>
                  <tr><td style={S.tc} className="text-muted">Expected Interest</td><td style={{ ...S.tcr, color: '#f59e0b' }}>{formatCurrency(loan.totalInterest || 0)}</td></tr>
                  <tr style={{ background: '#f8fafc' }}><td style={{ ...S.tc, fontWeight: 600 }}>Total Repayable</td><td style={{ ...S.tcr, fontWeight: 700 }}>{formatCurrency(loan.totalRepayable || 0)}</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="card p-3 mb-3">
            <h4 className="mb-2" style={{ fontSize: 14 }}>📊 Payment Breakdown</h4>
            <table style={{ fontSize: 13, width: '100%' }}>
              <thead>
                <tr style={V.tableHeaderBg}>
                  <th style={S.tc}>Component</th><th style={S.tcr}>Total</th>
                  <th style={S.tcr}>Paid</th><th style={S.tcr}>Outstanding</th><th style={S.tc}>Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ ...S.tc, fontWeight: 600 }}>Principal</td>
                  <td style={S.tcr}>{formatCurrency(loan.principalAmount)}</td>
                  <td style={{ ...S.tcr, color: '#059669' }}>{formatCurrency(loan.principalPaid || 0)}</td>
                  <td style={S.tcr}>{formatCurrency(summary?.outstandingPrincipal || loan.outstandingPrincipal || 0)}</td>
                  <td style={V.tcNote}></td>
                </tr>
                <tr>
                  <td style={{ ...S.tc, fontWeight: 600 }}>Interest</td>
                  <td style={{ ...S.tcr, color: '#f59e0b' }}>{formatCurrency(summary?.accruedInterest || loan.outstandingInterest || 0)}</td>
                  <td style={{ ...S.tcr, color: '#059669' }}>{formatCurrency(loan.interestPaid || 0)}</td>
                  <td style={S.tcr}>{formatCurrency(summary?.outstandingInterest || loan.outstandingInterest || 0)}</td>
                  <td style={V.tcNote}>{c.rateDescription}</td>
                </tr>
                <tr>
                  <td style={{ ...S.tc, fontWeight: 600 }}>Penalty</td>
                  <td style={{ ...S.tcr, color: (summary?.accruedPenalty || 0) > 0 ? '#dc2626' : '#94a3b8' }}>{formatCurrency(summary?.accruedPenalty || 0)}</td>
                  <td style={{ ...S.tcr, color: '#059669' }}>{formatCurrency(loan.penaltyPaid || 0)}</td>
                  <td style={{ ...S.tcr, color: (summary?.outstandingPenalty || 0) > 0 ? '#dc2626' : '#94a3b8' }}>{formatCurrency(summary?.outstandingPenalty || 0)}</td>
                  <td style={V.tcNote}>{c.penaltyTierDescription}</td>
                </tr>
                <tr style={V.maturityRow}>
                  <td style={{ ...S.tc, fontWeight: 600, color: '#7c3aed' }}>Remaining at Maturity</td>
                  <td style={{ ...S.tcr, fontWeight: 700, color: '#7c3aed' }}>{formatCurrency(loan.totalRepayable || 0)}</td>
                  <td style={{ ...S.tcr, fontWeight: 700, color: '#059669' }}>{formatCurrency(loan.totalPaid || 0)}</td>
                  <td style={{ ...S.tcr, fontWeight: 700, color: '#7c3aed' }}>{formatCurrency(remainingAtMaturity)}</td>
                  <td style={V.maturityNote}>Original P+I minus payments</td>
                </tr>
                <tr style={V.totalOwedRow}>
                  <td style={V.totalOwedLabel}>Total Owed Now</td>
                  <td colSpan={2}></td>
                  <td style={{ ...S.tcr, fontWeight: 700, fontSize: 15, color: totalAmountOwed > 0 ? '#dc2626' : '#059669' }}>{formatCurrency(totalAmountOwed)}</td>
                  <td style={V.tcNote}>P + I + Penalties (live)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid grid-2 gap-3">
            <div className="card p-3" style={S.rateTableCard}>
              <h4 className="mb-2" style={{ fontSize: 14 }}>📈 Interest Rate Table</h4>
              <InterestRateTable principal={loan.principalAmount || 0} currentDay={summary?.daysSinceDisbursement || 0} isFrozen={isClosed} />
            </div>
            <div className="card p-3" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
              <h4 className="mb-2" style={{ fontSize: 14 }}>⚡ Penalty Tiers (3-Tier System)</h4>
              <table style={{ fontSize: 13, width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={V.rateTableHeaderBg}>
                    <th style={S.tcs}>Tier</th><th style={S.tcs}>Percentage</th>
                    <th style={S.tcs}>When</th><th style={S.tcc}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {PENALTY_TIERS.filter(t => t.tier > 0).map(t => {
                    const activeTier = summary?.penaltyTierNumber || loan.currentPenaltyTier || 0;
                    const isActive = activeTier === t.tier;
                    return (
                      <tr key={t.tier} style={{ background: isActive ? t.color + '20' : 'transparent', fontWeight: isActive ? 700 : 400 }}>
                        <td style={S.tcs}>{t.label}</td>
                        <td style={{ ...S.tcc, color: t.color, fontWeight: 600 }}>{t.percent}%</td>
                        <td style={S.tcs}>{t.days}</td>
                        <td style={S.tcc}>{isActive ? '⚡ ACTIVE' : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ marginTop: 12, padding: '8px 12px', background: '#fef2f2', borderRadius: 6, fontSize: 12, color: '#991b1b' }}>
                <strong>Penalty Base:</strong> Outstanding Principal + Outstanding Interest (not on the full loan amount).
                {(summary?.penaltyTierNumber || 0) > 0 && (
                  <div style={{ marginTop: 4 }}>
                    Current: <strong>Tier {safeStr(summary.penaltyTierNumber)}</strong> — {formatCurrency(summary.accruedPenalty || 0)} penalty on {formatCurrency((summary.outstandingPrincipal || 0) + (summary.outstandingInterest || 0))} base ({safeStr(summary.daysIntoDefault || 0)} days into default).
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* TAB: REPAYMENT SCHEDULE */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {activeTab === 'schedule' && (
        <div className="p-3">
          {(!repaymentSchedule || repaymentSchedule.length === 0) ? (
            <div className="text-center p-4 text-muted">No repayment schedule available.</div>
          ) : (
            <div className="table-container" style={S.scrollContainer}>
              <table style={{ fontSize: 13 }}>
                <thead>
                  <tr style={V.stickyHeader}>
                    <th style={S.tcc}>#</th><th style={S.tcs}>Due Date</th>
                    <th style={S.tcsr}>Principal</th><th style={S.tcsr}>Interest</th>
                    <th style={S.tcsr}>Total Due</th><th style={S.tcsr}>Paid</th>
                    <th style={S.tcc}>Status</th><th style={S.tcs}>Paid Date</th>
                  </tr>
                </thead>
                <tbody>
                  {repaymentSchedule.map((inst, idx) => (
                    <tr key={idx} style={inst.status === 'paid' ? V.schedulePaid : inst.status === 'overdue' ? V.scheduleOverdue : undefined}>
                      <td style={S.tcc}>{safeStr(inst.installmentNumber)}</td>
                      <td style={S.tcs}>{formatDate(inst.dueDate)}</td>
                      <td style={S.tcsr}>{formatCurrency(inst.principalDue || 0)}</td>
                      <td style={S.tcsr}>{formatCurrency(inst.interestDue || 0)}</td>
                      <td style={V.scheduleTotalDue}>{formatCurrency(inst.totalDue || 0)}</td>
                      <td style={S.tcsr}>{formatCurrency(inst.totalPaid || 0)}</td>
                      <td style={V.scheduleStatusCenter}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                          background: getScheduleStatusColor(inst.status) + '20',
                          color: getScheduleStatusColor(inst.status),
                        }}>
                          {safeStr(inst.status).charAt(0).toUpperCase() + safeStr(inst.status).slice(1)}
                        </span>
                      </td>
                      <td style={S.tcs}>{inst.paidDate ? formatDate(inst.paidDate) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* TAB: INTEREST HISTORY */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {activeTab === 'interest' && (
        <div className="p-3">
          {ihTotal === 0 ? (
            <div className="text-center p-4 text-muted">No interest history recorded yet.</div>
          ) : (
            <>
              <div className="table-container" style={S.scrollContainer}>
                <table style={{ fontSize: 12 }}>
                  <thead>
                    <tr style={V.stickyHeader}>
                      <th style={S.tcc}>Day</th><th style={S.tcs}>Date</th>
                      <th style={S.tcc}>Rate</th><th style={{ ...S.tcc, color: '#f59e0b' }}>+Δ</th>
                      <th style={S.tcsr}>Cum. Interest</th><th style={S.tcsr}>Penalty %</th>
                      <th style={S.tcsr}>Penalty Amt</th><th style={S.tcc}>Tier</th>
                      <th style={S.tcc}>Overdue?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ihSlice.map(day => (
                      <tr key={day._id} style={(day.penaltyTier || 0) > 0 ? { background: '#fef2f2' } : undefined}>
                        <td style={S.tcc}>{safeStr(day.dayNumber)}</td>
                        <td style={S.tcs}>{formatDate(day.date)}</td>
                        <td style={{ ...S.tcc, fontWeight: 600, color: '#f59e0b' }}>{safeStr(day.dailyRate)}%</td>
                        <td style={{ ...S.tcc, color: (day.dailyRateIncrement || 0) > 0 ? '#dc2626' : '#94a3b8' }}>
                          {(day.dailyRateIncrement || 0) > 0 ? `+${day.dailyRateIncrement}%` : '—'}
                        </td>
                        <td style={S.tcsr}>{formatCurrency(day.interestAmount || 0)}</td>
                        <td style={{ ...S.tcc, color: (day.penaltyPercent || 0) > 0 ? '#dc2626' : '#94a3b8', fontWeight: (day.penaltyPercent || 0) > 0 ? 700 : 400 }}>
                          {(day.penaltyPercent || 0) > 0 ? `${day.penaltyPercent}%` : '—'}
                        </td>
                        <td style={{ ...S.tcsr, color: (day.penaltyAmount || 0) > 0 ? '#dc2626' : '#94a3b8' }}>
                          {formatCurrency(day.penaltyAmount || 0)}
                        </td>
                        <td style={S.tcc}>
                          {(day.penaltyTier || 0) > 0
                            ? <span style={getPenaltyTierBadgeStyle(day.penaltyTier)}>T{day.penaltyTier}</span>
                            : '—'}
                        </td>
                        <td style={S.tcc}>
                          {day.isOverdue
                            ? <span style={{ color: '#dc2626', fontWeight: 600 }}>{safeStr(day.daysOverdue)}d</span>
                            : <span style={{ color: '#94a3b8' }}>No</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination currentPage={ihPage} totalPages={ihPages} totalItems={ihTotal} onPageChange={(e) => {
                const p = parseInt(e.currentTarget.dataset.page, 10);
                if (p && p >= 1) setIhPage(p);
              }} />
            </>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* TAB: PAYMENT HISTORY */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {activeTab === 'payments' && (
        <div className="p-3">
          {payTotal === 0 ? (
            <div className="text-center p-4 text-muted">No payments recorded yet.</div>
          ) : (
            <>
              <div className="table-container" style={S.scrollContainer}>
                <table style={{ fontSize: 13 }}>
                  <thead>
                    <tr style={V.stickyHeader}>
                      <th style={S.tcs}>Date</th><th style={S.tcs}>Reference</th>
                      <th style={S.tcs}>Method</th><th style={S.tcsr}>Amount</th>
                      <th style={S.tcsr}>Principal</th><th style={S.tcsr}>Interest</th>
                      <th style={S.tcsr}>Penalty</th><th style={S.tcs}>Received By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paySlice.map((pay, idx) => {
                      const rb = pay.receivedBy || pay.collectedBy || {};
                      return (
                        <tr key={pay._id || idx}>
                          <td style={S.tcs}>{formatDateTime(pay.createdAt || pay.paymentDate)}</td>
                          <td style={{ ...S.tcs, fontFamily: 'monospace', fontSize: 12 }}>{safeStr(pay.referenceNumber || pay.transactionId) || '—'}</td>
                          <td style={S.tcs}>{safeStr(pay.paymentMethod) || '—'}</td>
                          <td style={{ ...S.tcsr, fontWeight: 700, color: '#059669' }}>{formatCurrency(pay.amount || 0)}</td>
                          <td style={S.tcsr}>{formatCurrency(pay.principalAmount || 0)}</td>
                          <td style={S.tcsr}>{formatCurrency(pay.interestAmount || 0)}</td>
                          <td style={S.tcsr}>{formatCurrency(pay.penaltyAmount || 0)}</td>
                          <td style={S.tcs}>{safeStr(rb.firstName) || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Pagination currentPage={payPage} totalPages={payPages} totalItems={payTotal} onPageChange={(e) => {
                const p = parseInt(e.currentTarget.dataset.page, 10);
                if (p && p >= 1) setPayPage(p);
              }} />
            </>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* TAB: CLOSURE DETAILS */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {activeTab === 'closure' && isClosed && (
        <ClosureDetailsTab loan={loan} />
      )}

      {interactionModalOpen && (
        <div role="presentation" onClick={(e) => { if (e.target === e.currentTarget) setInteractionModalOpen(false); }} style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div role="dialog" aria-modal="true" aria-labelledby="loan-interaction-title" className="card" style={{ width: '100%', maxWidth: 520, padding: 20, background: '#fff', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}><h3 id="loan-interaction-title" style={{ margin: 0 }}>💬 Log Loan Interaction</h3><button type="button" className="btn btn-outline btn-sm" onClick={() => setInteractionModalOpen(false)}>✕</button></div>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>Client: {safeStr(cl.firstName)} {safeStr(cl.lastName)} · Loan: {safeStr(loan.loanNumber)}</p>
            <label htmlFor="loan-interaction-type" style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Interaction type</label>
            <select id="loan-interaction-type" value={interactionType} onChange={(e) => setInteractionType(e.target.value)} style={{ width: '100%', padding: 10, border: '1px solid #cbd5e1', borderRadius: 6, marginBottom: 14 }}>
              <option value="other">Note / Other</option><option value="phone">Phone Call</option><option value="sms">SMS</option><option value="visit">Visit</option><option value="email">Email</option>
            </select>
            <label htmlFor="loan-interaction-content" style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Interaction details *</label>
            <textarea id="loan-interaction-content" value={interactionContent} onChange={(e) => setInteractionContent(e.target.value)} rows={4} placeholder="Describe the interaction..." style={{ width: '100%', boxSizing: 'border-box', padding: 10, border: '1px solid #cbd5e1', borderRadius: 6, resize: 'vertical' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}><button type="button" className="btn btn-outline" onClick={() => setInteractionModalOpen(false)}>Cancel</button><button type="button" className="btn btn-primary" disabled={savingInteraction || !interactionContent.trim()} onClick={handleSaveInteraction}>{savingInteraction ? 'Saving…' : 'Save Interaction'}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}